import { useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { uploadFile, updateMe } from "@/lib/api";

export const AvatarUploader = ({ size = "w-24 h-24", showText = true }) => {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) { toast.error("Use a JPG, PNG or WEBP image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setBusy(true);
    try {
      const r = await uploadFile(file);
      await updateMe({ picture_path: r.path });
      await refresh?.();
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="avatar-uploader" className="flex items-center gap-5">
      <label data-testid="avatar-upload-trigger" className={`relative ${size} rounded-full cursor-pointer group shrink-0`}>
        {user?.picture ? (
          <img data-testid="avatar-preview" src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover border-2 border-foreground"/>
        ) : (
          <div data-testid="avatar-preview" className="w-full h-full rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl font-semibold border-2 border-foreground">
            {user?.name?.[0]?.toUpperCase()}
          </div>
        )}
        <span className="absolute inset-0 rounded-full bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Camera className="w-6 h-6 text-white" strokeWidth={1.5}/>
        </span>
        {busy && <span className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center text-[10px] text-white mono">UPLOADING…</span>}
        <input data-testid="avatar-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={onPick} disabled={busy}/>
      </label>
      {showText && <div>
        <div className="font-semibold">Profile picture</div>
        <p className="text-xs text-muted-foreground mt-1">Click the photo to change it. JPG, PNG or WEBP, max 8MB.</p>
      </div>}
    </div>
  );
};
